import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faCheckDouble, faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import { UseNotificationInboxResult } from '@/core/notifications/hooks/use-notification-inbox';
import { formatUnreadCount } from '@/core/notifications/model';
import { useNotificationServiceContext } from '@/core/notifications/contexts/notification-service-context';
import { useOpenWidget } from '@/core/widget-navigation/export';
import { useIsMobile } from '@/shared/hooks/export';
import { LoadMoreSentinel, MenuList, MobileFullscreenPanel } from '@/shared/components/export';
import NotificationInboxEntry from './notification-inbox-entry';
import NotificationFilterPanel, { STATUS_OPTIONS } from './notification-filter-panel';

export default function NotificationFlyout({
  inbox,
  onClose,
}: {
  inbox: UseNotificationInboxResult;
  onClose?: () => void;
}) {
  const { subscriptions } = useNotificationServiceContext();
  const openWidget = useOpenWidget();
  const isMobile = useIsMobile(576);
  const unreadCount = inbox.totalUnreadCount;
  const markButtonDisabled = unreadCount === 0;

  const openManageSubscriptions = () => {
    onClose?.();
    openWidget('settings', 'notifications');
  };

  const notificationList = (
    <div className="position-relative flex-fill overflow-y-auto" style={{ minWidth: 0 }}>
      {inbox.loading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(0,0,0,0.25)', zIndex: 1 }}
        >
          <div className="spinner-border spinner-border-sm text-secondary" role="status" />
        </div>
      )}

      {!inbox.loading && inbox.notifications.length === 0 && (
        <div className={`p-4 text-center mutedText`} style={{ fontSize: 13 }}>
          No notifications
        </div>
      )}

      {inbox.notifications.map(n => (
        <NotificationInboxEntry
          key={n.id}
          notification={n}
          isMobile={isMobile}
          onMarkRead={() => inbox.markRead(n.notification.id)}
          onMarkUnread={() => inbox.markUnread(n.notification.id)}
          onClose={onClose}
        />
      ))}

      {inbox.hasMore && !inbox.loading && (
        // Remounted per page so a sentinel still in view after a short page triggers the next one
        <LoadMoreSentinel key={inbox.notifications.length} onVisible={inbox.loadMore} />
      )}
    </div>
  );

  const titleRow = (
    <div className="d-flex align-items-center gap-2">
      <span className="fw-bold" style={{ fontSize: 16 }}>
        Notifications
      </span>
      {unreadCount > 0 && (
        <span
          className="badge bg-danger"
          style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', minWidth: 16 }}
        >
          {formatUnreadCount(unreadCount)}
        </span>
      )}
    </div>
  );

  const actionsRow = (
    <div className="d-flex align-items-center gap-2">
      <button
        onClick={inbox.markAllRead}
        disabled={markButtonDisabled}
        title="Mark all read"
        className="btn btn-sm d-flex align-items-center gap-1"
        style={{
          fontSize: 12,
          fontWeight: 500,
          padding: '4px 12px',
          minHeight: 28,
          border: 'none',
          background: 'transparent',
          color: 'var(--bs-primary-400)',
          opacity: markButtonDisabled ? 0.5 : 1,
        }}
      >
        <FontAwesomeIcon icon={faCheckDouble} size="xs" />
        Mark all read
      </button>
    </div>
  );

  if (isMobile) {
    const selectorTriggerClassName =
      'btn btn-sm btn-outline-secondary w-100 d-flex align-items-center justify-content-between text-start';

    const selectorLabel = (text: string) => (
      <>
        <span className="text-truncate">{text}</span>
        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 11, opacity: 0.6 }} />
      </>
    );

    const applicationsValue =
      inbox.selectedPublishers.size === 0
        ? 'All'
        : inbox.selectedPublishers.size === 1
          ? inbox.applications.find(a => inbox.selectedPublishers.has(a.id))?.name
          : `${inbox.selectedPublishers.size} selected`;

    const subscriptionsValue =
      inbox.selectedSubscriptions.size === 0
        ? 'All'
        : inbox.selectedSubscriptions.size === 1
          ? subscriptions.find(s => inbox.selectedSubscriptions.has(s.id))?.name
          : `${inbox.selectedSubscriptions.size} selected`;

    const gearSlotWidth = 32;

    const filterBar = (
      <div className="d-flex flex-column gap-2 px-3 pt-2" style={{ paddingBottom: 10 }}>
        {/* Status — closes on selection, one full-width selector per row, sized to its own button */}
        <div className="d-flex align-items-center gap-2">
          <MenuList
            trigger={selectorLabel(
              `Status: ${STATUS_OPTIONS.find(o => o.value === inbox.statusFilter)?.label ?? 'All'}`,
            )}
            toggleClassName={`${selectorTriggerClassName} flex-fill`}
            matchReferenceWidth
            elevated
            items={STATUS_OPTIONS.map(opt => ({
              key: opt.value,
              label: opt.label,
              checked: inbox.statusFilter === opt.value,
              onSelect: () => inbox.setStatusFilter(opt.value),
            }))}
          />
          <div aria-hidden style={{ width: gearSlotWidth, flexShrink: 0 }} />
        </div>

        {/* Applications — stays open for multi-select */}
        {inbox.applications.length > 0 && (
          <div className="d-flex align-items-center gap-2">
            <MenuList
              trigger={selectorLabel(`Applications: ${applicationsValue}`)}
              toggleClassName={`${selectorTriggerClassName} flex-fill`}
              matchReferenceWidth
              elevated
              items={inbox.applications.map(app => ({
                key: app.id,
                label: app.name,
                selected: inbox.selectedPublishers.has(app.id),
                checkboxStyle: true,
                onSelect: () => inbox.togglePublisher(app.id),
              }))}
            />
            <div aria-hidden style={{ width: gearSlotWidth, flexShrink: 0 }} />
          </div>
        )}

        {/* Subscriptions — stays open for multi-select; manage-subscriptions gear sits alongside it */}
        {subscriptions.length > 0 && (
          <div className="d-flex align-items-center gap-2">
            <MenuList
              trigger={selectorLabel(`Alarms: ${subscriptionsValue}`)}
              toggleClassName={`${selectorTriggerClassName} flex-fill`}
              matchReferenceWidth
              elevated
              items={subscriptions.map(sub => ({
                key: sub.id,
                label: sub.name,
                selected: inbox.selectedSubscriptions.has(sub.id),
                checkboxStyle: true,
                onSelect: () => inbox.toggleSubscription(sub.id),
              }))}
            />
            <button
              type="button"
              onClick={openManageSubscriptions}
              title="Manage alarms"
              className="btn btn-sm rounded-pill d-flex align-items-center justify-content-center gap-1 manageSubscriptionsButton flex-shrink-0"
              style={{
                fontSize: 12,
                minHeight: 32,
                width: gearSlotWidth,
                border: '1px solid currentColor',
                background: 'transparent',
              }}
            >
              <FontAwesomeIcon icon={faGear} size="xs" />
            </button>
          </div>
        )}
      </div>
    );

    return (
      <MobileFullscreenPanel
        header={
          <div className="d-flex align-items-center justify-content-between w-100 gap-2">
            {titleRow}
            {actionsRow}
          </div>
        }
        onClose={onClose ?? (() => {})}
      >
        {/* Filters scroll together with the list instead of staying pinned */}
        {filterBar}
        {notificationList}
      </MobileFullscreenPanel>
    );
  }

  return (
    <div className="d-flex flex-column" style={{ maxHeight: '65vh' }}>
      {/* Desktop: side-by-side filter panel + list */}
      <div className="d-flex overflow-hidden" style={{ flex: 1, minHeight: 0 }}>
        <NotificationFilterPanel
          applications={inbox.applications}
          subscriptions={subscriptions}
          selectedPublishers={inbox.selectedPublishers}
          togglePublisher={inbox.togglePublisher}
          selectedSubscriptions={inbox.selectedSubscriptions}
          toggleSubscription={inbox.toggleSubscription}
          statusFilter={inbox.statusFilter}
          setStatusFilter={inbox.setStatusFilter}
          onManageSubscriptions={openManageSubscriptions}
        />

        <div className="d-flex flex-column" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <div className="d-flex align-items-center justify-content-between gap-2 px-3 pt-1 pb-2">
            {titleRow}
            <div className="d-flex align-items-center gap-2">
              {actionsRow}
              {onClose && (
                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="btn btn-sm p-0 border-0 bg-transparent d-flex align-items-center flex-shrink-0"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
            </div>
          </div>
          <div style={{ borderBottom: '1px solid var(--bs-border-color)', margin: '0 16px' }} />
          {notificationList}
        </div>
      </div>
    </div>
  );
}
